import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { api } from '@/lib/api';
import { Search, X } from 'lucide-react';

export default function AdminCustomers() {
    const [list, setList] = useState([]);
    const [q, setQ] = useState('');
    const [tier, setTier] = useState('');
    const [selected, setSelected] = useState(null);

    const load = () => {
        const p = new URLSearchParams();
        if (q) p.set('q', q);
        if (tier) p.set('tier', tier);
        api.get(`/admin/customers?${p.toString()}`).then(({ data }) => setList(data));
    };
    useEffect(load, [q, tier]);

    const view = async (id) => {
        const { data } = await api.get(`/admin/customers/${id}`);
        setSelected(data);
    };

    return (
        <AdminLayout>
            <div className="p-10" data-testid="admin-customers-page">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <div className="overline">Customers</div>
                        <h1 className="font-serif text-4xl text-navy mt-2">Customer 360</h1>
                    </div>
                    <div className="flex gap-3">
                        <div className="flex items-center gap-2 bg-white border border-border-subtle px-4 py-2 w-64">
                            <Search size={16} className="text-text-tertiary" />
                            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name/email/mobile" className="flex-1 outline-none text-sm" data-testid="customers-search" />
                        </div>
                        <select value={tier} onChange={(e) => setTier(e.target.value)} className="input-box max-w-[160px]" data-testid="customers-tier-filter">
                            <option value="">All Tiers</option>
                            <option value="silver">Silver</option>
                            <option value="gold">Gold</option>
                            <option value="platinum">Platinum</option>
                            <option value="black">Black</option>
                        </select>
                    </div>
                </div>
                <div className="bg-white border border-border-subtle">
                    <table className="w-full text-sm" data-testid="customers-table">
                        <thead className="bg-cream text-text-tertiary text-xs uppercase tracking-widest">
                            <tr><th className="text-left p-4">Member</th><th className="text-left p-4">Tier</th><th className="text-left p-4">Points</th><th className="text-left p-4">Lifetime Spend</th><th className="text-left p-4">Joined</th><th className="text-right p-4">Action</th></tr>
                        </thead>
                        <tbody>
                            {list.map((c) => (
                                <tr key={c.id} className="border-t border-border-subtle hover:bg-cream/50">
                                    <td className="p-4">
                                        <div className="text-navy font-medium">{c.name}</div>
                                        <div className="text-xs text-text-tertiary">{c.email} • {c.membership_id}</div>
                                    </td>
                                    <td className="p-4"><span className={`tier-${c.tier} px-3 py-1 text-xs uppercase tracking-widest font-semibold`}>{c.tier}</span></td>
                                    <td className="p-4 font-mono">{c.points_balance.toLocaleString()}</td>
                                    <td className="p-4">₹{c.lifetime_spend.toLocaleString('en-IN')}</td>
                                    <td className="p-4 text-text-tertiary text-xs">{new Date(c.created_at).toLocaleDateString('en-IN')}</td>
                                    <td className="p-4 text-right"><button onClick={() => view(c.id)} className="btn-link" data-testid={`view-customer-${c.id}`}>Open 360 →</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {selected && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setSelected(null)} data-testid="customer-360-modal">
                    <div className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="p-8 border-b border-border-subtle flex items-center justify-between">
                            <div>
                                <div className="overline">Customer 360</div>
                                <div className="font-serif text-3xl text-navy mt-1">{selected.customer.name}</div>
                                <div className="text-sm text-text-tertiary mt-1">{selected.customer.email} • {selected.customer.membership_id}</div>
                            </div>
                            <button onClick={() => setSelected(null)}><X size={24} /></button>
                        </div>
                        <div className="grid md:grid-cols-3 divide-x divide-border-subtle">
                            <Metric label="Tier" value={selected.customer.tier} />
                            <Metric label="Balance" value={selected.customer.points_balance.toLocaleString()} />
                            <Metric label="Lifetime Spend" value={`₹${selected.customer.lifetime_spend.toLocaleString('en-IN')}`} />
                        </div>
                        <div className="p-8">
                            <div className="overline mb-3">Recent Bills</div>
                            {selected.bills.length === 0 ? <div className="text-text-tertiary text-sm mb-6">No bills yet</div> : (
                                <div className="space-y-2 mb-6 max-h-48 overflow-y-auto">
                                    {selected.bills.map((b) => (
                                        <div key={b.id} className="flex justify-between text-sm border-b border-border-subtle py-2">
                                            <span>{b.brand_name || b.mall_name} — ₹{(b.total_amount || 0).toLocaleString('en-IN')}</span>
                                            <span className="text-xs text-text-tertiary uppercase">{b.status.replace('_', ' ')}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="overline mb-3">Points Ledger</div>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {selected.ledger.map((l) => (
                                    <div key={l.id} className="flex justify-between text-sm border-b border-border-subtle py-2">
                                        <span className="text-navy">{l.description}</span>
                                        <span className={`font-mono ${l.direction === 'credit' ? 'text-emerald-700' : 'text-rose-700'}`}>{l.direction === 'credit' ? '+' : '−'}{l.points}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}

function Metric({ label, value }) {
    return (
        <div className="p-6">
            <div className="overline">{label}</div>
            <div className="font-serif text-3xl text-navy capitalize mt-2">{value}</div>
        </div>
    );
}
